<template>
  <o-dialog
    ref="dialog"
    :title="$t('roster.addBreak')"
    :info="$t('components.schedule.actions.createBreak.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel', callback: cancel },
      { text: $t('roster.addBreak'), type: 'confirm', callback: confirm, disabled: isDisabled }
    ]"
    :portal="portal"
  >
    <div class="flex flex-col space-y-3 text-left mt-3">
      <o-field-group :label="$t('general.employee')" label-class="text-sm text-gray-600">
        <employee-select v-model="options.employee" :filter="employeeFilter" class="w-full" />
      </o-field-group>

      <o-field-group :label="$t('general.date')" date label-class="text-sm text-gray-600">
        <date-field v-model="options.date" :augmenter="Augmenters.YMD" />
      </o-field-group>

      <o-field-group :label="$t('general.time')" label-class="text-sm text-gray-600">
        <div class="flex items-start space-x-3">
          <div>
            <label class="text-xs text-gray-500 dark:text-gray-400">{{ $t('general.startTime') }}</label>
            <time-field v-model="options.time" :augmenter="Augmenters.minutes" />
          </div>
          <div>
            <label class="text-xs text-gray-500 dark:text-gray-400">{{ $t('general.duration') }}</label>
            <number-field v-model="options.duration" :min="0" :max="500" />
          </div>
        </div>
      </o-field-group>

      <o-field-group :label="$t('general.label')" label-class="text-sm text-gray-600">
        <text-field v-model="options.label" />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import EmployeeSelect from '@/components/employee/elements/Select.vue'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  TimeField,
  NumberField,
  DateField,
  TextField,
} from '@/components/fields'

interface Props {
  portal?: string
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'cancel': []
  'success': []
}>()

const { $api, $notification, $bus, $date } = useNuxtApp()

const dialog = ref<any>(null)

const options = reactive({
  date: new Date() as any,
  employee: null as any,
  time: 720,
  duration: 30,
  label: '',
})

const isDisabled = computed(() => {
  return !options.employee || !options.date || !options.time || !options.duration
})

function open({ employee = null, date = new Date(), time = 720, label = '', duration = 15 } = {}) {
  options.employee = employee
  options.date = $date.format(date)
  options.time = time
  options.duration = duration
  options.label = label

  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  return await createBreak()
}

function cancel() {
  emit('cancel')
  return true
}

function employeeFilter(employee: any) {
  return employee.isEmployeeType || (employee.isResourceType && employee.resourceRostered)
}

function formatDate(date: any) {
  const parsed = $date.parse(date)
  const utc = $date.getStartOfDayUTC(parsed)
  const timestamp = $date.timestamp(utc)

  return timestamp
}

async function createBreak() {
  const { employee, time, duration, label } = options
  const date = formatDate(options.date)

  try {
    const employeeBreak = await $api.roster.newBreak(date, employee, time, duration, label)

    emit('update:loading', false)

    if (employeeBreak) {
      $notification.toast({
        title: 'Created new break',
        variant: 'success',
      })

      $bus.emit.created('EmployeeBreak', employeeBreak)

      emit('success')
      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    console.log(e)
  }
}

defineExpose({ open })
</script>

<style lang="scss">
.roster-cell-container > div {
  height: 100%
}
</style>
